import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  FirestoreDataConverter,
  Unsubscribe,
} from 'firebase/firestore';
import type { StudyCase } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { OutputService } from './OutputService';
import { outputConverter } from '../converters/outputConverter';
import { studyCaseConverter as clientStudyCaseConverter } from '../converters/studyCaseConverter';

/**
 * StudyCaseService
 *
 * Type-safe service for StudyCase entity CRUD operations.
 * StudyCases are stored as subcollection under projects.
 *
 * Path: projects/{projectId}/studycases/{studyCaseId}
 *
 * Features:
 * - Basic CRUD operations (create, read, delete)
 * - Type-safe: only StudyCase types exposed, no Firebase types in public API
 * - Subcollection access via getOutputsService()
 * - Uses studyCaseConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { StudyCaseService } from '@/modules/firebase-core/services/StudyCaseService';
 *
 * const service = new StudyCaseService('project-id');
 *
 * // Create
 * const studyCase = await service.create({
 *   featureName: 'Test Feature',
 *   featureDescription: 'Description',
 *   minTheses: 3,
 *   maxStudyAgeYears: 5,
 *   outputLanguage: 'en',
 *   primaryBenefit: 'Benefit'
 * });
 *
 * // Read
 * const found = await service.get(studyCase.id!);
 *
 * // Delete
 * await service.delete(studyCase.id!);
 *
 * // Access outputs subcollection
 * const outputService = service.getOutputsService(studyCase.id!);
 * ```
 */
export class StudyCaseService {
  private readonly collectionName = 'studycases';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<StudyCase>;
  private readonly projectId: string;

  /**
   * Create a new StudyCaseService instance
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for StudyCase entity (defaults to client SDK converter)
   */
  constructor(projectId: string, converter: FirestoreDataConverter<StudyCase> = clientStudyCaseConverter) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Create a new study case
   *
   * @param data - Partial study case data (id will be auto-generated if not provided)
   * @returns {Promise<StudyCase>} The created study case with generated ID
   *
   * @example
   * ```typescript
   * const studyCase = await service.create({
   *   featureName: 'New Feature',
   *   featureDescription: 'A great feature',
   *   minTheses: 3,
   *   maxStudyAgeYears: 5,
   *   outputLanguage: 'en',
   *   primaryBenefit: 'Main benefit'
   * });
   * ```
   */
  async create(data: Partial<StudyCase>): Promise<StudyCase> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName);
      const docRef = doc(collectionRef).withConverter(this.converter);

      const studyCaseData: StudyCase = {
        ...data,
        id: docRef.id,
        createdAt: new Date(),
      } as StudyCase;

      await setDoc(docRef, studyCaseData);

      return studyCaseData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StudyCaseService.create');
      throw error;
    }
  }

  /**
   * Get a study case by ID
   *
   * @param id - StudyCase ID
   * @returns {Promise<StudyCase | null>} The study case or null if not found
   *
   * @example
   * ```typescript
   * const studyCase = await service.get('studycase-123');
   * if (studyCase) {
   *   console.log(studyCase.featureName);
   * }
   * ```
   */
  async get(id: string): Promise<StudyCase | null> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id)
        .withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as StudyCase;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StudyCaseService.get');
      throw error;
    }
  }

  /**
   * Delete a study case
   *
   * @param id - StudyCase ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('studycase-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StudyCaseService.delete');
      throw error;
    }
  }

  /**
   * Update a study case
   *
   * @param id - StudyCase ID
   * @param data - Partial study case data to merge
   * @returns {Promise<void>}
   */
  async update(id: string, data: Partial<StudyCase>): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await updateDoc(docRef, data as Record<string, unknown>);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StudyCaseService.update');
      throw error;
    }
  }

  /**
   * Subscribe to real-time study case updates
   *
   * @param onData - Callback with updated study cases array
   * @param onError - Optional error callback
   * @returns {Unsubscribe} Unsubscribe function
   */
  subscribe(
    onData: (items: StudyCase[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
      .withConverter(this.converter);
    const q = query(collectionRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as StudyCase)));
    }, onError);
  }

  /**
   * List all study cases for the project
   *
   * @param options - Query options (limit)
   * @returns {Promise<StudyCase[]>} Array of study cases
   *
   * @example
   * ```typescript
   * const studyCases = await service.list({ limit: 50 });
   * console.log('Found study cases:', studyCases.length);
   * ```
   */
  async list(options: { limit?: number } = {}): Promise<StudyCase[]> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
        .withConverter(this.converter);

      const pageLimit = options.limit || 20;
      const q = query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as StudyCase));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StudyCaseService.list');
      throw error;
    }
  }

  /**
   * Get OutputService for a study case's outputs subcollection
   *
   * @param studyCaseId - StudyCase ID
   * @returns {OutputService} OutputService instance scoped to this study case
   *
   * @example
   * ```typescript
   * const outputService = service.getOutputsService('studycase-123');
   *
   * // Now use outputService for outputs in this study case
   * const output = await outputService.create({});
   * ```
   */
  getOutputsService(studyCaseId: string): OutputService {
    return new OutputService(this.projectId, studyCaseId, outputConverter);
  }
}
